import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, BehaviorSubject, Subject, throwError, firstValueFrom, combineLatest } from 'rxjs';
import { catchError, distinctUntilChanged, filter, finalize, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import dayjs from 'dayjs';
import { LngLatLike } from 'mapbox-gl';

import { LibraryService, LibraryZone } from './library.service';
// Global API URL prefix constant so we never hard-code "/api" in service URLs.
import { APP_CONSTANTS } from '../../app.constants';

export type CircDetailAll = {
    Year: string;
    Category: string;
    Type: string;
    Count: number;
};

export type CircDetail = {
    Age: string;
    Category: string;
    Count: number;
    MatType: string;
    Year: string;
    collection: string;
};

export type WeedRecord = {
    Abbr: string;
    Age: string;
    BrowseAuthor: string;
    BrowseTitle: string;
    CallNumber: string;
    Category: string;
    Circulates: number;
    Code: number;
    Collection: string;
    Count: number;
    Dewey: string;
    Hotness: number;
    ItemRecordID: string;
    ItemStatusDescr: string;
    LCC: string;
    MARCPubDateOne: string;
    MARCTOMDescription: string;
    MatType: string;
    MaterialTypeDescription: string;
    SortISBN: string;
    SortUpc: string;
    TopCategory: string;
    bisacCode: string;
    id: number;
    insert_date: string;
    itembarcode: string;
    itembranchabbr: string;
    itemcreationdate: string;
    itemid: string;
    itemlifetimecirccount: number;
    itemytdcirccount: number;
    lastcheckoutrenewdate: string;
    libraryid: number;
    literal: string;
    name: string;
};

export interface Consortium {
    id: number;
    name: string;
    populationServed: number;
    coordinates: string;
}

export interface AccountGroup {
    id: number;
    name: string;
    populationServed: number;
    coordinates: string;
    libraryId?: number[];
    branchId?: number[];
}

export interface Library {
    name: string;
    id: number;
    accountId: number;
    cid?: number;
    cname?: string | null;
    groupId?: number;
    groupName?: string | null;
    settings: any;
    fiscalYear: string;
    q1?: dayjs.Dayjs; // this is only used in action-plan
    q2?: dayjs.Dayjs; // this is only used in action-plan
    q3?: dayjs.Dayjs; // this is only used in action-plan
    q4?: dayjs.Dayjs; // this is only used in action-plan
    coordinates?: string;
    censusLayer?: string;
    populationServed: number;
    cPopulationServed?: number;
    cCoordinates?: string;
    lgPopulationServed?: number;
    lgCoordinates?: string;
    lgBranchId?: number;
    patronsInServiceArea?: number;
}

export type CirculationTrend = {
    'Year 1': string;
    Year: number;
    Date: number;
    Count: number;
};

export interface GetCircByMonth {
    expired: boolean;
    data: [CirculationTrend[], { CircPreviousYear: number }[], { CircCurrentYear: number }[]];
}

interface SearchBiblio {
    lastLocation: string;
    start: string;
    end: string;
    collection: string;
    category?: string;
    returnValue?: string;
    deiCategory?: string;
}

export type MonitorChange = {
    currentLibrary: number;
    currentBranch: string;
};

/** Library, branch and zone published together, only once all three agree. */
export type FilterContext = MonitorChange & {
    zone: LibraryZone | null;
};

export type CriteriaChange = {
    currentAge: string;
    Category: string;
    MatType: string;
    deiCategory: string;
    period?: any;
};

export type Branch = {
    Name: string;
    ItemBranchAbbr: string;
    BranchId: number;
    populationServed: number;
    venueID: string;
    coordinates: string;
};

export type CollectionByMonth = {
    Period: string;
    Year: number;
    Month: number;
    Count: number;
};

export interface GetCollectionHistoryByCodes {
    expired: boolean;
    data: Collection[];
}

export interface Collection {
    CircCount?: number;
    CircPercent?: string | number;
    Collection: string;
    Abbr: string;
    Count: number;
    Percent?: string | number;
    DEIcatagory?: string;
    TotalCost?: number;
    CostPerItem?: number;
    CostPerCirc?: number;
}

export interface CircDate {
    Date: string;
    Count: number;
}

export interface CirculationByDateParams {
    zoneId?: string;
    consortium?: number;
    library: number;
    branch: string;
    age?: string;
    category?: string;
    matType?: string;
    collection?: string;
    deiCategory?: string;
}

export interface ByDateRangeAPIParams extends CirculationByDateParams {
    start: string;
    end?: string;
    topCategory?: string;
    // Separate field for the classification category filter (distinct from the
    // `category` field which maps to topCategory in some contexts)
    categoryFilter?: string;
    currentCollectionOnly?: boolean;
}

export interface CirculationTrendParams {
    consortium?: number;
    library: number;
    branch: string;
    age?: string;
    category?: string;
    matType?: string;
}

export interface GetCirculationByDateResponse {
    expired: boolean;
    data: [CircDate[], { YearCirc: number }[], { YearCircGroup: number }[]];
}

export interface GetCirculationByDate {
    expired: boolean;
    circDate: CircDate[];
    yearCircTotal: number;
    yearCircFiltered: number;
}

export interface GetLocationCirculation {
    expired: boolean;
    data: LocationCirculation[];
}

export interface LocationCirculation {
    Name: string;
    Count: number;
}

const MaterialTypes = [
    { label: 'filters.common.all', value: 'ALL' },
    { label: 'filters.materialTypes.Print', value: 'Print' },
    { label: 'filters.materialTypes.Movie', value: 'Movie' },
    { label: 'filters.materialTypes.Ebook', value: 'Ebook' },
    { label: 'filters.materialTypes.Audiobook', value: 'Audiobook' },
    { label: 'filters.materialTypes.Library of Things', value: 'Library of Things' },
    { label: 'filters.materialTypes.Magazine', value: 'Magazine' },
    { label: 'filters.materialTypes.Music', value: 'Music' },
    { label: 'filters.materialTypes.Video Games', value: 'Video Games' },
    { label: 'filters.materialTypes.Technology', value: 'Technology' },
    { label: 'filters.materialTypes.Kits', value: 'Kits' },
    { label: 'filters.materialTypes.Read Along', value: 'Read Along' },
    { label: 'filters.materialTypes.Other', value: 'Other' }
];

@Injectable({ providedIn: 'root' })
export class ReportService {
    // Shared API URL prefix sourced from APP_CONSTANTS so the "/api" mount point
    // is defined in one place and can be changed globally if needed.
    protected readonly API_PREFIX = APP_CONSTANTS.API_PREFIX;
    currentLibrary = null;
    currentConsortium = null;
    inConsortiumID = null;
    currentAccountGroup = null;
    accountGroups: AccountGroup[] = [];
    consortiums: Consortium[] = [];
    libraries: Library[] = [];
    branches: Branch[] = [];
    currentBranch = null;
    currentBranchId: number | null = null;
    currentPeriod = null;
    criteriaChange: BehaviorSubject<CriteriaChange>;
    lastLibrary = -1;
    lastBranch: string | number = -1;
    collectionPerformance;
    periods: { name: string; id: string }[];
    searchBiblio: SearchBiblio = {
        lastLocation: '',
        start: '',
        end: '',
        collection: '',
        category: '',
        returnValue: null,
        deiCategory: ''
    };
    validBranches = [];

    // The zone lives in a plain field so a library/branch change can clear it
    // without emitting. zoneSelection$ only tells zone-aware UI what changed;
    // data loaders listen to filterContext$, which is published once the
    // library, branch and zone all agree.
    private zoneState: LibraryZone | null = null;
    // Set when a library/branch change cleared the zone silently; the UI is
    // told after the matching context update has been published.
    private zoneResetPending = false;
    private readonly zoneSelection$ = new BehaviorSubject<LibraryZone | null>(null);
    readonly zoneChange$ = this.zoneSelection$.asObservable();

    private readonly filterContextSubject = new BehaviorSubject<FilterContext>({
        currentLibrary: null,
        currentBranch: null,
        zone: null
    });
    /**
     * One event per consistent library/branch/zone state. Unlike combining
     * monitorChange with zoneChange$, it never emits the new zone with the old
     * library (or the reverse) while a change is half applied.
     */
    readonly filterContext$ = this.filterContextSubject.asObservable();

    get currentZone(): LibraryZone | null {
        return this.zoneState;
    }

    // Zone selects on screen. The shared zone outlives them, so it only scopes
    // the page (e.g. its export names) while at least one is shown.
    private zoneFilterCount = 0;
    private readonly zoneFilterVisibilitySubject = new Subject<void>();
    /**
     * Fires when the first zone filter appears or the last one goes away, i.e. when
     * activeZoneName may change without a filter context change. Kept off
     * filterContext$ so data loaders don't reload for it.
     */
    readonly zoneFilterVisibilityChange$ = this.zoneFilterVisibilitySubject.asObservable();

    setZoneFilterShown(shown: boolean): void {
        const wasShown = this.zoneFilterCount > 0;
        this.zoneFilterCount = Math.max(0, this.zoneFilterCount + (shown ? 1 : -1));
        if (wasShown !== this.zoneFilterCount > 0) this.zoneFilterVisibilitySubject.next();
    }

    /** Whether a zone filter is on screen, so the selected zone scopes the page. */
    get isZoneFilterShown(): boolean {
        return this.zoneFilterCount > 0;
    }

    /**
     * The zone that scopes the page's data, titles and export names: the selected zone while
     * a zone filter is shown, otherwise null. Zone-aware code reads this rather than
     * currentZone so they all agree on when the kept zone applies.
     */
    get activeZone(): LibraryZone | null {
        return this.isZoneFilterShown ? this.zoneState : null;
    }

    /** The active zone's name, or ''. */
    get activeZoneName(): string {
        return this.activeZone?.name ?? '';
    }

    /** A user zone selection. A zone forces the branch to ALL. */
    changeZone(zone: LibraryZone | null | undefined): void {
        const selected = zone?.id && zone.id !== 'ALL' ? zone : null;
        const branchReset = !!selected && this.currentBranch !== 'ALL';
        if (selected) {
            // Not changeBranch: that clears the zone being selected.
            this.currentBranch = 'ALL';
            this.lastBranch = 'ALL';
            this.currentBranchId = 0;
        }
        // By id: a restored zone is the same zone reloaded as a new object.
        if (this.zoneState?.id === selected?.id && !branchReset) return;
        this.zoneState = selected;
        this.zoneResetPending = false;
        if (branchReset) {
            // monitorChange must hold the branch in effect, or charts that dedupe
            // it skip the refresh when the old branch is picked again. The
            // constructor subscriber publishes the combined filter context.
            this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
        } else {
            this.publishFilterContext({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
        }
        this.zoneSelection$.next(selected);
    }

    /**
     * Clears the zone for an upcoming library/branch change without emitting
     * anything. The caller's monitorChange emission publishes the combined
     * context, and only then is the zone UI told (see the constructor).
     */
    private clearZoneSilently(): boolean {
        if (!this.zoneState) return false;
        this.zoneState = null;
        this.zoneResetPending = true;
        return true;
    }

    /** Publishes a zone reset that had no library/branch change to carry it. */
    private publishZoneReset(): void {
        this.zoneResetPending = false;
        this.publishFilterContext({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
        this.zoneSelection$.next(null);
    }

    private publishFilterContext(context: MonitorChange): void {
        this.filterContextSubject.next({ ...context, zone: this.zoneState });
    }

    librariesLoading$ = new BehaviorSubject<boolean>(false);
    /** Fires as soon as a library is chosen, before branch lists finish loading. */
    libraryChangeStart$ = new Subject<number>();
    monitorChange = new BehaviorSubject<MonitorChange>({
        currentLibrary: this.currentLibrary,
        currentBranch: this.currentBranch
    });
    $libraries = new BehaviorSubject<Library[]>([]);
    private pendingLibraryLoad$: Observable<any> | null = null;
    accountGroupChange$ = new BehaviorSubject<number | null>(null);
    consortiumChange$ = new BehaviorSubject<number | null>(null);

    constructor(
        private http: HttpClient,
        private libraryService: LibraryService
    ) {
        // Every monitorChange — including ones emitted outside changeLibrary,
        // e.g. a consortium switch or logout — becomes one filterContext$
        // event. A zone belongs to one library, so a library change drops it
        // first; the zone UI is told only after the combined context is out,
        // so nothing ever sees the new zone state paired with the old library.
        // This is the first monitorChange subscriber, so it runs before any
        // page reacts to the same emission.
        this.monitorChange.pipe(takeUntilDestroyed()).subscribe((context) => {
            if (context.currentLibrary !== this.filterContextSubject.value.currentLibrary) {
                this.clearZoneSilently();
            }
            this.publishFilterContext(context);
            if (this.zoneResetPending) {
                this.zoneResetPending = false;
                this.zoneSelection$.next(null);
            }
        });

        this.periods = [
            { name: 'Annually', id: 'annually' },
            { name: 'Quarterly', id: 'quarterly' }
        ];
        this.currentPeriod = 'annually';
        this.collectionPerformance = {
            selectAges: [
                {
                    label: 'filters.common.all',
                    value: 'ALL'
                },
                {
                    label: 'filters.ages.Adult',
                    value: 'Adult'
                },
                {
                    label: 'filters.ages.Young Adult',
                    value: 'Young Adult'
                },
                {
                    label: 'filters.ages.Children',
                    value: 'Children'
                },
                {
                    label: 'filters.ages.Other',
                    value: 'Other'
                }
            ]
        };
        this.collectionPerformance.selectAge = {
            selected: this.collectionPerformance.selectAges[0].value
        };

        this.collectionPerformance.selectCategorys = [
            {
                label: 'filters.common.all',
                value: 'ALL'
            },
            {
                label: 'filters.categories.Fiction',
                value: 'Fiction'
            },
            {
                label: 'filters.categories.Nonfiction',
                value: 'Nonfiction'
            },
            {
                label: 'filters.categories.Other',
                value: 'Other'
            }
        ];
        this.collectionPerformance.selectCategory = {
            selected: this.collectionPerformance.selectCategorys[0].value
        };

        // Material Types are configured based on availability
        this.collectionPerformance.selectMatTypes = [
            {
                label: 'filters.common.all',
                value: 'ALL'
            }
        ];
        this.collectionPerformance.selectMatType = {
            selected: this.collectionPerformance.selectMatTypes[0].value
        };

        this.criteriaChange = new BehaviorSubject<{
            currentAge: string;
            Category: string;
            MatType: string;
            deiCategory: string;
        }>({
            currentAge: this.collectionPerformance.selectAge,
            Category: this.collectionPerformance.selectCategory,
            MatType: this.collectionPerformance.selectMatType,
            deiCategory: this.collectionPerformance.deiCategory
        });

        this.monitorChange
            .pipe(
                distinctUntilChanged(
                    (prev, curr) =>
                        // only continue if the branch or the library actually changed
                        prev.currentBranch === curr.currentBranch && prev.currentLibrary === curr.currentLibrary
                ),
                filter((change) => !!change.currentBranch && !!change.currentLibrary),
                switchMap(() => this.getValidMatTypes()),
                map((res) => res.data.map((item) => item.MatType))
            )
            .subscribe((validMatTypes) => {
                const currentMatType = this.collectionPerformance.selectMatType.selected;
                this.collectionPerformance.selectMatType.selected = null;

                this.collectionPerformance.selectMatTypes = MaterialTypes.filter((type) =>
                    validMatTypes.includes(type.value)
                );

                this.collectionPerformance.selectMatTypes.unshift({
                    label: 'filters.common.all',
                    value: 'ALL'
                });

                this.collectionPerformance.selectMatTypes.forEach((element, index) => {
                    if (element.value === currentMatType) {
                        this.collectionPerformance.selectMatType = {
                            selected: this.collectionPerformance.selectMatTypes[index].value
                        };
                    }
                });

                // if we still don't have a selected, use the first
                if (!this.collectionPerformance.selectMatType.selected) {
                    this.collectionPerformance.selectMatType = {
                        selected: this.collectionPerformance.selectMatTypes[0].value
                    };
                }

                // Emit the updated criteria so subscribers are notified of the material type change
                // This ensures components listening to criteriaChange get the latest values after library switches
                this.criteriaChange.next({
                    currentAge: this.collectionPerformance.selectAge,
                    Category: this.collectionPerformance.selectCategory,
                    MatType: this.collectionPerformance.selectMatType,
                    deiCategory: this.collectionPerformance.deiCategory
                });
            });

        // need to set the library in the library service on changes
        // TODO: have library state completely handled in the library service
        combineLatest([
            this.monitorChange.pipe(
                filter(({ currentLibrary, currentBranch }) =>
                    !!currentLibrary && !!currentBranch
                ),
                distinctUntilChanged((prev, curr) =>
                    prev.currentLibrary === curr.currentLibrary &&
                    prev.currentBranch === curr.currentBranch
                )
            ),
            this.$libraries.pipe(filter((libraries) => libraries?.length > 0))
        ])
            .pipe(
                takeUntilDestroyed()
            )
            .subscribe(([{ currentLibrary, currentBranch }, libraries]) => {
                const library = libraries.find((l) => l.id === currentLibrary);
                if (!library) {
                    console.error(`Library not found: ${currentLibrary}`);
                    return;
                }

                this.libraryService.setCurrentLibrary(library);
                this.libraryService.setCurrentBranches(this.branches);

                const branch = this.branches.find((b) => b.ItemBranchAbbr === currentBranch);
                if (branch) {
                    this.libraryService.setCurrentBranch(branch);
                }
            });
    }

    public capitalizeFirstLetter(input: string): string {
        return input && input.length ? input.charAt(0).toUpperCase() + input.slice(1).toLowerCase() : input;
    }

    public capitalizeSecondLetter(input: string): string {
        return input && input.length
            ? input.slice(0, 1).toLowerCase() + input.charAt(1).toUpperCase() + input.slice(2).toLowerCase()
            : input;
    }

    private async userLog(message: string): Promise<void> {
        // putting and getting the current user in session because depending on auth service
        // will cause a circular dependency. We should pull these `userLog` methods out
        // into a logging service and remove the session storage...
        try {
            const user = JSON.parse(sessionStorage.getItem('sessionUser'));
            if (!user || !user.userName) return;
            await firstValueFrom(
                this.http.post(`${this.API_PREFIX}/login/userLog`, {
                    user: user.userName,
                    message: message,
                    libraryid: this.currentLibrary,
                    branchid: this.currentBranch
                })
            );
        } catch (_) {
            console.error('Failed to send user log');
        }
    }

    public resetCriteria(): void {
        this.collectionPerformance.selectAge.selected = 'ALL';
        this.collectionPerformance.selectCategory.selected = 'ALL';
        this.collectionPerformance.selectMatType.selected = 'ALL';
    }

    public isCriteriaAll(): boolean {
        if (
            this.collectionPerformance.selectAge.selected === 'ALL' &&
            this.collectionPerformance.selectCategory.selected === 'ALL' &&
            this.collectionPerformance.selectMatType.selected === 'ALL'
        ) {
            return true;
        }
        return false;
    }

    public changeCriteria(): void {
        this.userLog('Change Criteria');
        this.criteriaChange.next({
            currentAge: this.collectionPerformance.selectAge,
            Category: this.collectionPerformance.selectCategory,
            MatType: this.collectionPerformance.selectMatType,
            period: this.currentPeriod,
            deiCategory: this.collectionPerformance.deiCategory
        });
    }

    public getBranchName(): string {
        let branchName = '';
        this.branches.forEach((branch) => {
            if (branch.ItemBranchAbbr === this.currentBranch) {
                branchName = branch.Name;
            }
        });
        return branchName;
    }

    public getBranchMap(): {[key: string]: string} {
        const branchMap = {};
        this.branches.forEach(branch => {
            branchMap[branch.ItemBranchAbbr] = branch.Name;
        });
        return branchMap;
    }

    public getLibraryName(libraryid = this.currentLibrary): string {
        let libName = '';
        this.libraries.forEach((library) => {
            if (library.id === libraryid) {
                libName = library.name;
            }
        });
        return libName;
    }

    public getCurrentLibrary(): Library {
        if (!this.libraries.length) {
            return;
        }
        const library = this.libraries.find((l) => l.id === this.currentLibrary);
        if (library && !library?.fiscalYear) library.fiscalYear = '1-1';
        return library;
    }

    public resetGroups(): void {
        if (this.currentLibrary === -1) {
            if (this.libraries[0].id === -1) {
                this.libraries.shift();
            }
            this.$libraries.next(this.libraries);
            this.currentAccountGroup = 0;
            this.accountGroupChange$.next(0);
            this.currentConsortium = 0;
            this.consortiumChange$.next(0);
            this.currentBranch = 'ALL';
            this.changeLibrary({ id: this.libraries[0].id });
        }
    }

    public changeAccountGroup($event): void {
        if (!$event.value) {
            if (this.libraries[0].id === -1) {
                this.libraries.shift();
            }
            if (this.currentLibrary === -1) {
                this.changeLibrary({ id: 1 });
            }
            this.$libraries.next(this.libraries);
            this.accountGroupChange$.next($event.value);
            return;
        }
        if (this.libraries[0].id !== -1) {
            this.libraries.unshift({
                name: 'All',
                id: -1,
                accountId: -1,
                fiscalYear: '',
                populationServed: 0,
                groupId: $event.value,
                settings: ''
            });
        } else {
            this.libraries[0].groupId = $event.value;
        }

        this.$libraries.next(this.libraries);
        this.accountGroupChange$.next($event.value);
        this.lastLibrary = -1;
        this.currentLibrary = -1;
        this.currentBranch = -1;

        this.userLog('Change Group');
        this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
    }

    public changeConsortium($event): void {
        if (!$event.value) {
            if (this.libraries[0].id === -1) {
                this.libraries.shift();
            }
            if (this.currentLibrary === -1) {
                this.changeLibrary({ id: this.libraries[0].id });
            }
            this.$libraries.next(this.libraries);
            this.consortiumChange$.next($event.value);
            return;
        }

        if (this.libraries[0].id !== -1) {
            this.libraries.unshift({
                name: 'All',
                id: -1,
                accountId: -1,
                fiscalYear: '',
                populationServed: 0,
                cid: $event.value,
                settings: ''
            });
        } else {
            this.libraries[0].cid = $event.value;
        }

        this.$libraries.next(this.libraries);
        this.consortiumChange$.next($event.value);
        this.lastLibrary = -1;
        this.currentLibrary = -1;
        this.currentBranch = -1;

        this.userLog('Change Consortium');
        this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
    }

    private createConsortiums(groups): void {
        this.consortiums = [];
        this.accountGroups = [];
        let hash = {};
        this.libraries.forEach((library) => {
            if (library.cname && !hash[library.cname]) {
                hash[library.cname] = library;
                this.consortiums.push({
                    id: library.cid,
                    name: library.cname,
                    populationServed: library.cPopulationServed,
                    coordinates: library.cCoordinates
                });
            }
        });

        if (this.consortiums.length > 0) {
            this.consortiums = this.consortiums.sort((a, b) =>
                a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
            );
            this.consortiums.unshift({ id: 0, name: 'None', populationServed: null, coordinates: null });
            this.currentConsortium = 0;
        }
        if (this.consortiums.length === 2 && this.libraries.length === 1) this.consortiums = [];
        hash = {};
        groups.forEach((group) => {
            if (group.groupName && hash[group.groupName]) {
                this.accountGroups.forEach((ag, index) => {
                    if (ag.id === group.groupId && group.lgBranchId) {
                        this.accountGroups[index].branchId.push(group.lgBranchId);
                    }
                    if (ag.id === group.groupId && group.lgLibraryId) {
                        this.accountGroups[index].libraryId.push(group.lgLibraryId);
                    }
                });
            }
            if (group.groupName && !hash[group.groupName]) {
                hash[group.groupName] = group;
                this.accountGroups.push({
                    id: group.groupId,
                    name: group.groupName,
                    libraryId: group.lgLibraryId ? [-1, group.lgLibraryId] : [-1],
                    branchId: group.lgBranchId ? [group.lgBranchId] : [],
                    populationServed: group.lgPopulationServed,
                    coordinates: group.lgCoordinates
                });
            }
        });

        if (this.accountGroups.length > 0) {
            this.accountGroups.unshift({ id: 0, name: 'None', coordinates: null, populationServed: null });
            this.currentAccountGroup = 0;
        }
    }

    public loadLibraries(forceRefresh = false): Observable<[Library[], any[]]> {
        if (forceRefresh) {
            this.pendingLibraryLoad$ = null;
        }

        if (!this.pendingLibraryLoad$) {
            this.pendingLibraryLoad$ = this.http.get<[Library[], []]>(`${this.API_PREFIX}/report/getLibraries`).pipe(
                tap(([libraries, groups]) => {
                    this.libraries = libraries;
                    this.createConsortiums(groups);
                    this.$libraries.next(libraries);
                }),
                catchError((err) => {
                    this.pendingLibraryLoad$ = null;
                    return this.handleError(err);
                }),
                finalize(() => {
                    this.pendingLibraryLoad$ = null;
                }),
                shareReplay(1)
            );
        }

        return this.pendingLibraryLoad$;
    }

    /**
     * Kick off library loading early so the HTTP request runs in
     * parallel with other initialization work (e.g. checkSession).
     * Callers of loadLibraries() will reuse the in-flight request.
     */
    public preloadLibraries(): void {
        if (this.libraries.length > 0) return;
        this.loadLibraries().subscribe({
            error: (err) => console.error('Failed to preload libraries', err)
        });
    }

    async initUserLibraryBranch(library: number, branch: string): Promise<void> {
        this.librariesLoading$.next(true);

        try {
            await firstValueFrom(this.loadLibraries());

            // check if the passed library matches the list the user has access to
            const hasLibaryAccess = this.libraries.map((l => l.id)).includes(library);

            this.currentLibrary = hasLibaryAccess ? library : this.libraries[0].id;
            this.inConsortiumID = this.getCurrentLibrary()?.cid;
            this.lastLibrary = this.currentLibrary;

            const { data } = await firstValueFrom(this.getBranchByLibrary());
            this.branches = data;

            if (this.validBranches.length > 0) {
                this.branches = this.branches.filter((b) => {
                    return this.validBranches.includes(b.BranchId);
                });
            }

            // check if the passed branch matches the list user has access to
            const hasBranchAccess = this.branches.map((b) => b.ItemBranchAbbr).includes(branch);

            if (this.branches.length > 1) {
                this.currentBranch = hasBranchAccess ? branch : 'ALL';
                this.branches.unshift({
                    Name: 'All',
                    ItemBranchAbbr: 'ALL',
                    BranchId: 0,
                    populationServed: null,
                    venueID: null,
                    coordinates: null
                });
            } else {
                // there is only one branch to choose
                this.currentBranch = this.branches[0].ItemBranchAbbr;
            }

            this.lastBranch = this.currentBranch;
            this.currentBranchId = this.getBranchId(this.currentBranch);
            this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
        } catch (error) {
            console.error('initUserLibraryBranch: failed to initialize library/branch', error);

            if (this.libraries.length === 0){
                throw Error('Failed to load any libraries');
            }

            this.currentLibrary = this.libraries?.length ? this.libraries[0].id : library;
            this.currentBranch = 'ALL';
            this.lastLibrary = this.currentLibrary;
            this.lastBranch = this.currentBranch;
            this.currentBranchId = 0;
            this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
        } finally {
            this.librariesLoading$.next(false);
        }
    }

    public changeLibrary($event, refresh = null): void {
        // Cleared without emitting: the monitorChange below carries the reset,
        // so loaders never fetch the old library with the zone removed.
        const zoneCleared = this.clearZoneSilently();
        if (this.currentAccountGroup && $event.value === -1) {
            this.currentLibrary = -1;
            this.currentBranch = 'ALL';
            this.currentBranchId = 0;
            this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
            return;
        }

        this.currentLibrary = $event.value;
        if (this.currentLibrary === undefined) {
            this.currentLibrary = $event.id || 32;
        }

        if (this.currentLibrary === this.lastLibrary && !refresh) {
            // Same library re-selected: no monitorChange follows, so the zone
            // reset is published on its own.
            if (zoneCleared) this.publishZoneReset();
            return;
        }
        this.inConsortiumID = this.getCurrentLibrary()?.cid;
        this.lastLibrary = this.currentLibrary;
        this.libraryChangeStart$.next(this.currentLibrary);
        this.getBranchByLibrary(refresh).subscribe((branches) => {
            this.branches = branches.data;
            if (this.branches.length > 1) {
                this.branches.unshift({
                    Name: 'All',
                    ItemBranchAbbr: 'ALL',
                    BranchId: 0,
                    populationServed: null,
                    venueID: null,
                    coordinates: null
                });
            }
            if (this.validBranches.length > 0) {
                this.branches = this.branches.filter((branch) => {
                    if (this.validBranches.includes(branch.BranchId)) return true;
                    else return false;
                });
            }
            this.currentBranch = this.branches.length > 0 ? this.branches[0].ItemBranchAbbr : 'ALL';
            this.lastBranch = this.currentBranch;
            this.currentBranchId = this.getBranchId(this.currentBranch);
            if (this.currentAccountGroup && this.getCurrentAccountGroup().branchId.length) {
                this.currentBranchId = this.getFirstBranchInGroup();
                this.currentBranch = this.getBranchAbbr(this.currentBranchId);
            }
            this.userLog('Change Library');
            this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
        });
    }

    public changeBranch(): void {
        const zoneCleared = this.clearZoneSilently();
        if (this.currentBranch === this.lastBranch) {
            // e.g. picking "All" while a zone forced the branch to ALL: only
            // the zone changes, so publish that as the single event.
            if (zoneCleared) this.publishZoneReset();
            return;
        }
        this.lastBranch = this.currentBranch;
        if (!this.currentBranch?.length) this.currentBranchId = null;
        else {
            this.currentBranchId = this.getBranchId(this.currentBranch);
        }
        this.userLog('Change Branch');
        this.monitorChange.next({ currentLibrary: this.currentLibrary, currentBranch: this.currentBranch });
    }

    public getFirstBranchInGroup(): number {
        const branchFound = this.branches.find((branch) => {
            if (this.getCurrentAccountGroup().branchId.includes(branch.BranchId)) return true;
            else return false;
        });
        if (branchFound) return branchFound.BranchId;
        else return null;
    }

    public getBranchId(branchAbbr: string): number {
        const branchFound = this.branches.find((branch) => {
            if (branch.ItemBranchAbbr === branchAbbr) return true;
            else return false;
        });
        if (branchFound) return branchFound.BranchId;
        else return null;
    }

    public getBranchAbbr(branchId: number): string {
        const branchFound = this.branches.find((branch) => {
            if (branch.BranchId === branchId) return true;
            else return false;
        });
        if (branchFound) return branchFound.ItemBranchAbbr;
        else return null;
    }

    public getBranchPopulationServed(): number {
        const branchFound = this.branches.find((branch) => {
            if (branch.ItemBranchAbbr === this.currentBranch) return true;
            else return false;
        });
        if (branchFound) return branchFound.populationServed;
        else return null;
    }

    public getBranchVenueID(): string {
        const branchFound = this.branches.find((branch) => {
            if (branch.ItemBranchAbbr === this.currentBranch) return true;
            else return false;
        });
        if (branchFound) return branchFound.venueID;
        else return null;
    }

    public hasAnyBranchWithVenueID(): boolean {
        if (!this.branches || this.branches.length === 0) {
            return false;
        }
        return this.branches.some((branch) => branch.venueID && branch.venueID.trim().length > 0);
    }

    public getBranchCoordinates(): LngLatLike {
        const branchFound = this.branches.find((branch) => {
            if (branch.ItemBranchAbbr === this.currentBranch) return true;
            else return false;
        });
        if (branchFound?.coordinates?.length) return JSON.parse(branchFound.coordinates);
        else return null;
    }

    public getBranchByLibrary(refresh: string | null = null): Observable<{ expired: boolean; data: Branch[] }> {
        return this.http
            .get<{ expired: boolean; data: Branch[] }>(`${this.API_PREFIX}/report/getBranchByLibrary`, {
                params: {
                    library: this.currentLibrary,
                    ...(refresh !== null && { refresh }) // Only include refresh if it's not null
                }
            })
            .pipe(catchError(this.handleError));
    }

    // Required to handle any error type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private handleError(error: Response): Observable<any> {
        // localStorage.removeItem('currentLibrary');
        // localStorage.removeItem('currentBranch');
        console.error('ApiService::handleError', error);
        return throwError(error);
    }

    // this one here
    public getCirculationTrend(params: CirculationTrendParams): Observable<GetCircByMonth> {
        return this.http
            .get<GetCircByMonth>(`${this.API_PREFIX}/report/getCircByMonth`, {
                params: { ...params }
            })
            .pipe(catchError(this.handleError));
    }

    public getCurrentAccountGroup(): AccountGroup | null {
        return this.accountGroups.find((account) => account.id === this.currentAccountGroup) ?? null;
    }

    public getCurrentConsortium(): Consortium | null {
        return this.consortiums.find((c) => c.id === this.currentConsortium) ?? null;
    }

    public accountGroupBranchesValue(): string {
        return '[]';
    }

    public getCirculationInHouseTrend(): Observable<{ expired: boolean; data: CirculationTrend[] }> {
        return this.http
            .get<{ expired: boolean; data: CirculationTrend[] }>(`${this.API_PREFIX}/inHouseStats/getCircInHouseByMonth`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCollectionCreatedByMonth(): Observable<{ expired: boolean; data: CollectionByMonth[] }> {
        const apiUrl = this.collectionPerformance.deiCategory
            ? `${this.API_PREFIX}/report/getDEICollectionHistoryCreatedByMonth`
            : `${this.API_PREFIX}/report/getCollectionCreatedByMonth`;
        return this.http
            .get<{ expired: boolean; data: CollectionByMonth[] }>(apiUrl, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    DEICategory: this.collectionPerformance.deiCategory ? this.collectionPerformance.deiCategory : ''
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCollectionHistoryCollectionCode(): Observable<GetCollectionHistoryByCodes> {
        const apiUrl = this.collectionPerformance.deiCategory
            ? `${this.API_PREFIX}/report/getDEICollectionHistoryCollectionCode`
            : `${this.API_PREFIX}/report/getCollectionHistoryCollectionCode`;
        return this.http
            .get<GetCollectionHistoryByCodes>(apiUrl, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    DEICategory: this.collectionPerformance.deiCategory ? this.collectionPerformance.deiCategory : '',
                    collection: 'ALL'
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getValidMatTypes(): Observable<any> {
        const apiUrl = `${this.API_PREFIX}/libraries/getValidMatTypes`;
        return this.http
            .get<any>(apiUrl, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCollectionHistoryForDateRange(
        params: ByDateRangeAPIParams
    ): Observable<{ expired: boolean; data: WeedRecord[] }> {
        const url = this.searchBiblio.deiCategory
            ? `${this.API_PREFIX}/report/getDEICollectionHistoryForDateRange`
            : `${this.API_PREFIX}/report/getCollectionHistoryForDateRange`;
        return this.http
            .get<{ expired: boolean; data: WeedRecord[] }>(url, {
                params: {
                    consortium: params.consortium,
                    library: params.library,
                    branch: params.branch,
                    start: params.start,
                    end: params.end,
                    age: params.age,
                    category: params.category,
                    matType: params.matType,
                    DEICategory: params.deiCategory,
                    collection: params.collection
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCircReportAll(): Observable<{ expired: boolean; data: CircDetailAll[] }> {
        return this.http
            .get<{ expired: boolean; data: CircDetailAll[] }>(`${this.API_PREFIX}/report/getCircReportAll`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCircReportDetail(): Observable<{ expired: boolean; data: CircDetail[] }> {
        return this.http
            .get<{ expired: boolean; data: CircDetail[] }>(`${this.API_PREFIX}/report/getCircReportDetail`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCollectionAgeBreakdown(): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/report/getCollectionAgeBreakdown`, {
                params: {
                    zoneId: this.currentZone?.id || 'ALL',
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getFrequencyByCollection(): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/report/getFrequencyByCollection`, {
                params: {
                    zoneId: this.currentZone?.id || 'ALL',
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    period: this.currentPeriod
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCirculationByDate(params: CirculationByDateParams): Observable<GetCirculationByDate> {
        return this.http
            .get<GetCirculationByDateResponse>(`${this.API_PREFIX}/report/getCirculationByDate`, {
                params: { ...params }
            })
            .pipe(
                map((res) => {
                    const result: GetCirculationByDate = {
                        expired: res.expired,
                        circDate: res?.data?.[0] as CircDate[] ?? [],
                        yearCircTotal: res?.data?.[1]?.[0]?.YearCirc ?? 0,
                        yearCircFiltered: res?.data?.[2]?.[0]?.YearCircGroup ?? 0
                    };
                    return result;
                }),
                catchError(this.handleError)
            );
    }

    public getHoldsByDate(): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/report/getHoldsByDate`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getNewItemUtilization(): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/report/getNewItemUtilization`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    period: this.currentPeriod
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getNewItemUtilizationPattern(): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/report/getNewItemUtilizationPattern`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    period: this.currentPeriod
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCircForDateRange(params: ByDateRangeAPIParams): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/report/getCircForDateRange`, {
                params: {
                    consortium: params.consortium,
                    library: params.library,
                    branch: params.branch,
                    start: params.start,
                    age: params.age,
                    collection: params.collection,
                    category: params.category ?? 'ALL',
                    topCategory: params.topCategory ?? 'ALL',
                    matType: params.matType,
                    end: params.end,
                    currentCollectionOnly: params?.currentCollectionOnly ?? false
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getCircInHouseForDateRange(params: ByDateRangeAPIParams): Observable<any> {
        return this.http
            .get(`${this.API_PREFIX}/inHouseStats/getCircInHouseForDateRange`, {
                params: {
                    consortium: params.consortium,
                    library: params.library,
                    branch: params.branch,
                    zoneId: params.zoneId || 'ALL',
                    start: params.start,
                    age: params.age,
                    category: params.category,
                    matType: params.matType,
                    collection: params.collection,
                    end: params.end
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getLocationCirculation(collection: string): Observable<GetLocationCirculation> {
        return this.http
            .get<GetLocationCirculation>(`${this.API_PREFIX}/report/getLocationCirculation`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    collection: collection
                }
            })
            .pipe(catchError(this.handleError));
    }

    public getLocationCirculationInverse(collection: string): Observable<GetLocationCirculation> {
        return this.http
            .get<GetLocationCirculation>(`${this.API_PREFIX}/report/getLocationCirculationInverse`, {
                params: {
                    consortium: this.currentConsortium,
                    library: this.currentLibrary,
                    branch: this.currentBranch,
                    age: this.collectionPerformance.selectAge.selected,
                    category: this.collectionPerformance.selectCategory.selected,
                    matType: this.collectionPerformance.selectMatType.selected,
                    collection: collection
                }
            })
            .pipe(catchError(this.handleError));
    }
}
